import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Currency,
  classNames,
} from '@sushiswap/ui'
import { BagIcon } from '@sushiswap/ui/icons/BagIcon'
import React, { type FC } from 'react'
import type { ConcentratedLiquidityPositionWithV3Pool } from 'src/lib/wagmi/hooks/positions/types'
import { formatPercent, formatUSD } from 'sushi'
import { type EvmCurrency, unwrapEvmToken } from 'sushi/evm'
import { PortfolioInfoRow } from '../portfolio-info-row'

interface PortfolioV3PositionsProps {
  positions: ConcentratedLiquidityPositionWithV3Pool[]
}

function getRange(
  position: ConcentratedLiquidityPositionWithV3Pool,
): 'IN_RANGE' | 'OUT_OF_RANGE' {
  const { tickCurrent } = position.pool
  if (tickCurrent >= position.tickLower && tickCurrent < position.tickUpper) {
    return 'IN_RANGE'
  }
  return 'OUT_OF_RANGE'
}

export const PortfolioV3Positions: FC<PortfolioV3PositionsProps> = ({
  positions,
}) => (
  <AccordionItem value="v3" className="!border-0">
    <AccordionTrigger className="px-5 underline-offset-2">
      {`V3 Positions (${positions.length})`}
    </AccordionTrigger>
    <AccordionContent className="cursor-default">
      {positions.map((position) => {
        const token0: EvmCurrency = unwrapEvmToken(position.token0)
        const token1: EvmCurrency = unwrapEvmToken(position.token1)
        const range = getRange(position)

        return (
          <PortfolioInfoRow
            key={`${position.chainId}:${position.tokenId}`}
            chainId={position.chainId}
            href={`/pool/v3/${position.address}/${position.tokenId}`}
            icon={
              <Currency.IconList iconWidth={24} iconHeight={24}>
                <Currency.Icon disableLink currency={token0} />
                <Currency.Icon disableLink currency={token1} />
              </Currency.IconList>
            }
            leftContent={
              <React.Fragment>
                <div className="text-sm font-medium overflow-hidden overflow-ellipsis">
                  {token0.symbol} / {token1.symbol}
                </div>
                <div className="flex items-center gap-x-1">
                  <div className="text-muted-foreground text-xs">
                    {`V3-${formatPercent(position.fee / 1000000)}`}
                  </div>
                  <div
                    className={classNames(
                      range === 'OUT_OF_RANGE' ? 'bg-yellow/10' : 'bg-green/10',
                      'px-2 py-1 flex items-center gap-1 rounded-full',
                    )}
                  >
                    <div
                      className={classNames(
                        range === 'OUT_OF_RANGE' ? 'bg-yellow' : 'bg-green',
                        'w-3 h-3 rounded-full',
                      )}
                    />
                    <span className="text-xs font-medium text-muted-foreground">
                      {range === 'OUT_OF_RANGE' ? 'Out of Range' : 'In Range'}
                    </span>
                  </div>
                </div>
              </React.Fragment>
            }
            rightContent={
              <React.Fragment>
                <div className="text-sm font-medium overflow-hidden overflow-ellipsis">
                  {formatUSD(position.position.positionUSD)}
                </div>
                <div className="text-xs text-muted-foreground flex gap-x-0.5 items-center justify-end overflow-hidden overflow-ellipsis">
                  <BagIcon width={10} height={9} />
                  {formatUSD(position.position.unclaimedUSD)}
                </div>
              </React.Fragment>
            }
          />
        )
      })}
    </AccordionContent>
  </AccordionItem>
)
